/**
 * THE BREAKING-CONSUMER LIST — one rendering, both surfaces.
 *
 * Two places refuse a change on the impact gate's verdict: the Data area's record edit
 * (`DataArea.tsx`) and `Add fields`' publish (`SchemaAddFields.tsx`). Both listed
 * `verdict.breaking` inline, and the two copies had already drifted — the Data area printed
 * `(no line located)` for a row with no lines and the publish panel printed nothing, so the same
 * verdict read as two different refusals depending on which control the operator had opened.
 *
 * THE ROW'S REAL SHAPE. `impact_of_change` emits `{kind, name, path, errors, lines}`
 * (`validate_data_refs.py:1624`) — it has never emitted `line` or `why`, so reading those printed
 * `path:undefined — undefined` and told the operator nothing about a refusal that is correct. A
 * gate that cannot say WHERE is not the usable invariant ADR-168 D7 treats it as.
 *
 * `lines` is a list and MAY be empty — the checker could not locate the error in the file's text.
 * Saying so beats printing nothing, because a missing line reads as a rendering bug otherwise.
 * That is the behaviour `DataArea.tsx` argued for and it is now the single behaviour. And the key
 * carries the index: two breaking rows in one file are ordinary.
 */

import type { DataImpactRow } from '../api/client'

export interface BreakingConsumerListProps {
  /** The verdict's `breaking` rows. Absent and empty both render an empty list — the caller
   *  decides whether a refusal is warranted, this only says what broke. */
  rows?: DataImpactRow[]
  /** The host's own type scale. The two surfaces size this list differently and neither is
   *  wrong, so the classes stay the caller's. */
  className?: string
}

export function BreakingConsumerList({ rows, className }: BreakingConsumerListProps) {
  return (
    <ul className={className}>
      {(rows ?? []).map((b, i) => (
        <li key={`${b.kind}:${b.name}:${b.path}:${i}`}>
          {b.kind} <strong>{b.name}</strong> — <code className="font-mono">{b.path}</code>
          {b.lines && b.lines.length > 0 ? `:${b.lines.join(', ')}` : ' (no line located)'}
          {b.errors && b.errors.length > 0 && ` — ${b.errors.join('; ')}`}
        </li>
      ))}
    </ul>
  )
}
